import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Plus,
  Search,
  CheckCircle2,
  Circle,
  Clock,
  Calendar,
  Target,
  Phone,
  Mail,
  MessageSquare,
  Video,
  Flag,
  Building2,
  AlertCircle,
  Filter,
  ArrowUpRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TASK_TYPES = [
  { value: 'call', label: 'Call', icon: Phone, color: 'bg-blue-100 text-blue-700' },
  { value: 'email', label: 'Email', icon: Mail, color: 'bg-purple-100 text-purple-700' },
  { value: 'meeting', label: 'Meeting', icon: Video, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'follow_up', label: 'Follow-up', icon: MessageSquare, color: 'bg-amber-100 text-amber-700' },
  { value: 'demo', label: 'Demo', icon: Target, color: 'bg-pink-100 text-pink-700' },
  { value: 'other', label: 'Other', icon: Flag, color: 'bg-slate-100 text-slate-700' },
];

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-700 border-slate-200'
};

const Activities = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    type: 'call',
    priority: 'medium',
    due_date: '',
    lead_id: '',
  });

  useEffect(() => {
    fetchTasks();
    fetchLeads();
  }, [filterStatus]);

  const fetchTasks = async () => {
    try {
      let url = `${API_URL}/api/tasks`;
      if (filterStatus !== 'all') {
        url += `?status=${filterStatus}`;
      }
      const response = await axios.get(url, getAuthHeaders());
      setTasks(response.data);
    } catch (error) {
      // Generate mock data
      generateMockTasks();
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leads`, getAuthHeaders());
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const generateMockTasks = () => {
    const mockTasks = [
      {
        id: '1',
        title: 'Follow up with Metro Health Clinic',
        description: 'Discuss EHR implementation timeline',
        type: 'call',
        priority: 'high',
        status: 'pending',
        due_date: new Date(Date.now() + 86400000).toISOString(),
        lead_id: 'lead-1',
        lead_name: 'Metro Health Clinic',
        created_at: new Date().toISOString()
      },
      {
        id: '2',
        title: 'Send proposal to CardioCare Associates',
        description: 'RCM service proposal with pricing',
        type: 'email',
        priority: 'high',
        status: 'pending',
        due_date: new Date(Date.now() + 172800000).toISOString(),
        lead_id: 'lead-2',
        lead_name: 'CardioCare Associates',
        created_at: new Date().toISOString()
      },
      {
        id: '3',
        title: 'Demo scheduled with Primary Care Partners',
        description: 'CCM platform walkthrough',
        type: 'demo',
        priority: 'medium',
        status: 'pending',
        due_date: new Date(Date.now() + 259200000).toISOString(),
        lead_id: 'lead-3',
        lead_name: 'Primary Care Partners',
        created_at: new Date().toISOString()
      },
      {
        id: '4',
        title: 'Contract review call',
        description: 'Final contract terms discussion',
        type: 'meeting',
        priority: 'high',
        status: 'completed',
        due_date: new Date(Date.now() - 86400000).toISOString(),
        lead_id: 'lead-4',
        lead_name: 'OrthoPlus Medical',
        created_at: new Date().toISOString(),
        completed_at: new Date().toISOString()
      },
      {
        id: '5',
        title: 'Check in with Dr. Park',
        description: 'Quarterly business review',
        type: 'call',
        priority: 'low',
        status: 'pending',
        due_date: new Date(Date.now() + 604800000).toISOString(),
        lead_id: 'lead-5',
        lead_name: 'Pediatric Care Center',
        created_at: new Date().toISOString()
      }
    ];

    if (filterStatus === 'all') {
      setTasks(mockTasks);
    } else if (filterStatus === 'completed') {
      setTasks(mockTasks.filter(t => t.status === 'completed'));
    } else {
      setTasks(mockTasks.filter(t => t.status === 'pending'));
    }
  };

  const handleToggleTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newStatus = task.status === 'completed' ? 'pending' : 'completed';

    try {
      await axios.patch(`${API_URL}/api/tasks/${taskId}`, { status: newStatus }, getAuthHeaders());
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
    } catch (error) {
      // Update locally for demo
    }

    setTasks(prev => prev.map(t => 
      t.id === taskId 
        ? { ...t, status: newStatus, completed_at: newStatus === 'completed' ? new Date().toISOString() : null }
        : t
    ));
  };

  const handleAddTask = async () => {
    try {
      await axios.post(`${API_URL}/api/tasks`, newTask, getAuthHeaders());
      toast.success('Task created successfully');
    } catch (error) {
      // Add locally for demo
      const selectedLead = leads.find(l => l.id === newTask.lead_id);
      const task = {
        id: `task-${Date.now()}`,
        ...newTask,
        status: 'pending',
        lead_name: selectedLead?.organization_name || 'Unknown',
        created_at: new Date().toISOString()
      };
      setTasks(prev => [task, ...prev]);
      toast.success('Task created successfully');
    }

    setShowAddModal(false);
    setNewTask({
      title: '',
      description: '',
      type: 'call',
      priority: 'medium',
      due_date: '',
      lead_id: '',
    });
  };

  const getTaskTypeInfo = (type) => {
    return TASK_TYPES.find(t => t.value === type) || TASK_TYPES[5];
  };

  const getDueDateStatus = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'Overdue', color: 'text-red-600 bg-red-50' };
    if (diffDays === 0) return { label: 'Today', color: 'text-amber-600 bg-amber-50' };
    if (diffDays === 1) return { label: 'Tomorrow', color: 'text-blue-600 bg-blue-50' };
    if (diffDays <= 7) return { label: `In ${diffDays} days`, color: 'text-slate-600 bg-slate-50' };
    return { label: new Date(dueDate).toLocaleDateString(), color: 'text-slate-600 bg-slate-50' };
  };

  const filteredTasks = tasks.filter(task => {
    const searchLower = searchQuery.toLowerCase();
    return (
      task.title?.toLowerCase().includes(searchLower) ||
      task.lead_name?.toLowerCase().includes(searchLower) ||
      task.description?.toLowerCase().includes(searchLower)
    );
  });

  const pendingTasks = filteredTasks.filter(t => t.status === 'pending');
  const completedTasks = filteredTasks.filter(t => t.status === 'completed');
  const overdueTasks = pendingTasks.filter(t => new Date(t.due_date) < new Date());
  const todayTasks = pendingTasks.filter(t => {
    const due = new Date(t.due_date);
    const today = new Date();
    return due.toDateString() === today.toDateString();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-500">Loading tasks...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8"
      data-testid="activities-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Tasks & Activities</h1>
          <p className="text-slate-500 mt-1">Manage your follow-ups, calls, and meetings</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Task Title</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="e.g., Follow up with lead"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={newTask.type} onValueChange={(v) => setNewTask({ ...newTask, type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Related Lead</Label>
                  <Select value={newTask.lead_id} onValueChange={(v) => setNewTask({ ...newTask, lead_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select lead..." />
                    </SelectTrigger>
                    <SelectContent>
                      {leads.slice(0, 20).map(lead => (
                        <SelectItem key={lead.id} value={lead.id}>{lead.organization_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddTask} className="bg-[#1B365D] hover:bg-[#1B365D]/90">
                Create Task
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-apple">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Tasks</p>
                <p className="mt-2 text-3xl font-bold text-[#1B365D]">{tasks.length}</p>
              </div>
              <div className="p-3 bg-[#1B365D]/10 rounded-xl">
                <Target className="w-6 h-6 text-[#1B365D]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Due Today</p>
                <p className="mt-2 text-3xl font-bold text-amber-600">{todayTasks.length}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Overdue</p>
                <p className="mt-2 text-3xl font-bold text-red-600">{overdueTasks.length}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-xl">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Completed</p>
                <p className="mt-2 text-3xl font-bold text-emerald-600">{completedTasks.length}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-full"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 rounded-full">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tasks List */}
      <Tabs defaultValue="pending" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-full">
          <TabsTrigger value="pending" className="rounded-full data-[state=active]:bg-white">
            Pending ({pendingTasks.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="rounded-full data-[state=active]:bg-white">
            Completed ({completedTasks.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card className="border-0 shadow-apple">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {pendingTasks.length > 0 ? (
                  pendingTasks.map(task => {
                    const typeInfo = getTaskTypeInfo(task.type);
                    const dueStatus = getDueDateStatus(task.due_date);
                    
                    return (
                      <div
                        key={task.id}
                        className="p-4 hover:bg-slate-50 transition-colors"
                        data-testid={`task-${task.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <button
                            onClick={() => handleToggleTask(task.id)}
                            className="mt-1 flex-shrink-0"
                          >
                            <Circle className="w-5 h-5 text-slate-300 hover:text-[#1B365D] transition-colors" />
                          </button>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-medium text-slate-900">{task.title}</h3>
                              <Badge className={typeInfo.color}>
                                <typeInfo.icon className="w-3 h-3 mr-1" />
                                {typeInfo.label}
                              </Badge>
                              <Badge className={PRIORITY_COLORS[task.priority]}>
                                {task.priority}
                              </Badge>
                            </div>
                            {task.description && (
                              <p className="text-sm text-slate-500 mt-1">{task.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2">
                              {task.lead_name && (
                                <button
                                  onClick={() => navigate(`/leads/${task.lead_id}`)}
                                  className="flex items-center gap-1 text-sm text-[#0071E3] hover:underline"
                                >
                                  <Building2 className="w-4 h-4" />
                                  {task.lead_name}
                                  <ArrowUpRight className="w-3 h-3" />
                                </button>
                              )}
                              <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${dueStatus.color}`}>
                                <Clock className="w-3 h-3" />
                                {dueStatus.label}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-12 text-center text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No pending tasks. You're all caught up!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed">
          <Card className="border-0 shadow-apple">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {completedTasks.length > 0 ? (
                  completedTasks.map(task => {
                    const typeInfo = getTaskTypeInfo(task.type);
                    
                    return (
                      <div
                        key={task.id}
                        className="p-4 hover:bg-slate-50 transition-colors opacity-75"
                        data-testid={`task-${task.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <button
                            onClick={() => handleToggleTask(task.id)}
                            className="mt-1 flex-shrink-0"
                          >
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          </button>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-slate-500 line-through">{task.title}</h3>
                              <Badge className={typeInfo.color}>
                                {typeInfo.label}
                              </Badge>
                            </div>
                            {task.lead_name && (
                              <div className="flex items-center gap-1 mt-2 text-sm text-slate-400">
                                <Building2 className="w-4 h-4" />
                                {task.lead_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-12 text-center text-slate-500">
                    <p>No completed tasks yet.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default Activities;
